import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type FilterType = 'all' | 'expense' | 'income';

interface TransactionRecord {
  id: string;
  type: 'withdraw' | 'dispose' | 'payment' | 'deposit';
  typeLabel: string;
  title: string;
  amount: number;
  status: string;
  statusLabel: string;
  time: string;
  icon: string;
  isExpense: boolean;
}

const statusMap: Record<string, string> = {
  pending: '处理中',
  completed: '已完成',
  confirmed: '已完成',
  paid: '已支付',
  rejected: '已驳回',
  failed: '失败',
};

const typeConfig: Record<string, { label: string; icon: string; isExpense: boolean }> = {
  withdraw: { label: '提现', icon: 'ri-wallet-3-line', isExpense: false },
  payment: { label: '订单付款', icon: 'ri-shopping-cart-line', isExpense: true },
  deposit: { label: '入金', icon: 'ri-download-line', isExpense: false },
};

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<TransactionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    setRecords([]);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setRecords([]);
        setLoading(false);
        return;
      }

      const list: TransactionRecord[] = [];

      // 1. 提现记录
      const { data: withdraws } = await supabase
        .from('withdraw_records')
        .select('id, method, amount, status, create_time')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      for (const w of withdraws || []) {
        const cfg = typeConfig.withdraw;
        list.push({
          id: `W-${w.id}`,
          type: 'withdraw',
          typeLabel: cfg.label,
          title: `提现到${w.method || '银行卡'}`,
          amount: Number(w.amount) || 0,
          status: w.status || 'pending',
          statusLabel: statusMap[w.status || 'pending'] || '处理中',
          time: w.create_time || '',
          icon: cfg.icon,
          isExpense: cfg.isExpense,
        });
      }

      // 2. 入金记录
      const { data: deposits } = await supabase
        .from('deposits')
        .select('id, amount, channel, status, create_time')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      for (const d of deposits || []) {
        const cfg = typeConfig.deposit;
        list.push({
          id: `DE-${d.id}`,
          type: 'deposit',
          typeLabel: cfg.label,
          title: `${d.channel || '银行卡'}入金`,
          amount: Number(d.amount) || 0,
          status: d.status || 'pending',
          statusLabel: statusMap[d.status || 'pending'] || '处理中',
          time: d.create_time || '',
          icon: cfg.icon,
          isExpense: cfg.isExpense,
        });
      }

      // 3. 订单付款记录（只保留保证金、工费、仓管费）
      const { data: payments } = await supabase
        .from('payments')
        .select('id, order_id, product, amount, status, create_time, payment_method')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      for (const p of payments || []) {
        const isAllowed =
          p.payment_method === '工费' ||
          p.payment_method === '仓管费' ||
          p.payment_method === '保证金';
        if (!isAllowed) continue;

        const cfg = typeConfig.payment;
        let title = `购买 ${p.product || ''}`;
        if (p.payment_method === '工费') title = `工费 ${p.product || ''}`;
        else if (p.payment_method === '仓管费') title = `仓管费 ${p.product || ''}`;
        else if (p.payment_method === '保证金') title = `保证金 ${p.product || ''}`;
        list.push({
          id: `P-${p.id}`,
          type: 'payment',
          typeLabel: cfg.label,
          title,
          amount: Number(p.amount) || 0,
          status: p.status || 'pending',
          statusLabel: statusMap[p.status || 'pending'] || '处理中',
          time: p.create_time || '',
          icon: cfg.icon,
          isExpense: cfg.isExpense,
        });
      }

      // 按时间倒序排序
      list.sort((a, b) => {
        const ta = a.time ? new Date(a.time).getTime() : 0;
        const tb = b.time ? new Date(b.time).getTime() : 0;
        return tb - ta;
      });

      setRecords(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setRefreshKey((prev) => prev + 1);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return records;
    if (filter === 'expense') return records.filter((r) => r.isExpense);
    return records.filter((r) => !r.isExpense);
  }, [records, filter]);

  const totalExpense = useMemo(
    () => records.filter((r) => r.isExpense).reduce((sum, r) => sum + r.amount, 0),
    [records],
  );

  const totalIncome = useMemo(
    () => records.filter((r) => !r.isExpense).reduce((sum, r) => sum + r.amount, 0),
    [records],
  );

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'completed':
      case 'confirmed':
      case 'paid':
        return 'bg-green-50 text-green-600';
      case 'rejected':
      case 'failed':
        return 'bg-red-50 text-red-500';
      default:
        return 'bg-amber-50 text-amber-600';
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    try {
      const d = new Date(timeStr);
      if (isNaN(d.getTime())) return timeStr;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch {
      return timeStr;
    }
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">资金流水</span>
        <div className="w-8" />
      </div>

      {/* Summary Cards */}
      <div className="mx-4 mt-4 grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-brown-500 text-xs">累计支出</p>
          <p className="text-red-500 text-lg font-bold mt-1">
            -¥{totalExpense.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-brown-500 text-xs">累计收入</p>
          <p className="text-green-600 text-lg font-bold mt-1">
            +¥{totalIncome.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mx-4 mt-4 bg-white rounded-xl p-1 flex shadow-sm">
        {[
          { key: 'all' as FilterType, label: '全部' },
          { key: 'expense' as FilterType, label: '支出' },
          { key: 'income' as FilterType, label: '收入' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
              filter === tab.key
                ? 'bg-gold-500 text-brown-950'
                : 'text-brown-500 hover:text-brown-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mx-4 mt-4">
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-brown-400 text-sm mt-3">加载中...</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <i className="ri-error-warning-line text-brown-300 text-4xl"></i>
            <p className="text-brown-500 text-sm mt-3">加载失败</p>
            <button
              onClick={fetchData}
              className="mt-3 text-gold-600 text-sm font-medium cursor-pointer"
            >
              重试
            </button>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-brown-300 text-4xl"></i>
            <p className="text-brown-400 text-sm mt-3">暂无记录</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((record) => (
              <div
                key={record.id}
                className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  record.isExpense ? 'bg-red-50' : 'bg-green-50'
                }`}>
                  <span className={`w-5 h-5 flex items-center justify-center ${
                    record.isExpense ? 'text-red-400' : 'text-green-500'
                  }`}>
                    <i className={`${record.icon} text-lg`}></i>
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-brown-800 text-sm font-medium truncate">{record.title}</p>
                    <span className={`text-sm font-bold flex-shrink-0 ${
                      record.isExpense ? 'text-brown-900' : 'text-green-600'
                    }`}>
                      {record.isExpense ? '-' : '+'}¥{record.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-brown-400 text-xs">{record.typeLabel}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getStatusStyle(record.status)}`}>
                        {record.statusLabel}
                      </span>
                    </div>
                    <span className="text-brown-400 text-xs">{formatTime(record.time)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}